// API base URL - use production URL in development and relative path in production
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'https://kalahats.com/api';

/**
 * Fetch all products
 * @returns {Promise<Array>} Array of products
 */
export async function fetchProducts() {
  try {
    const response = await fetch(`${API_BASE_URL}/products`);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
}

/**
 * Fetch a product by ID
 * @param {number|string} id - Product ID
 * @returns {Promise<Object|null>} Product object or null if not found
 */
export async function fetchProductById(id) {

  try {
    const response = await fetch(`${API_BASE_URL}/products/${id}`);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching product with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Create a new product
 * @param {Object} productData - Product data
 * @returns {Promise<Object>} Created product
 */
export async function addProduct(productData) {
  try {
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
}

/**
 * Update an existing product
 * @param {number|string} id - Product ID
 * @param {Object} productData - Updated product data
 * @returns {Promise<Object>} Updated product
 */
export async function editProduct(id, productData) {
  try {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error updating product with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Delete a product
 * @param {number|string} id - Product ID
 * @returns {Promise<boolean>} Success status
 */
export async function removeProduct(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.success;
  } catch (error) {
    console.error(`Error deleting product with ID ${id}:`, error);
    throw error;
  }
}

/**
 * Filter products by criteria
 * @param {Array} products - Array of products to filter
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered products
 */
export function filterProducts(products, filters = {}) {
  return products.filter(product => {
    // Filter by category if specified
    if (filters.category && product.category !== filters.category) {
      return false;
    }
    
    // Filter by price range if specified
    if (filters.minPrice && product.price < filters.minPrice) {
      return false;
    }
    if (filters.maxPrice && product.price > filters.maxPrice) {
      return false;
    }
    
    // Filter by availability if specified
    if (filters.inStock === true && !product.inStock) {
      return false;
    }
    
    // Filter by search term if specified
    if (filters.search && !productMatchesSearch(product, filters.search)) {
      return false;
    }
    
    return true;
  });
}

/**
 * Check if a product matches a search term
 * @param {Object} product - Product to check
 * @param {string} searchTerm - Search term
 * @returns {boolean} Whether the product matches the search term
 */
function productMatchesSearch(product, searchTerm) {
  if (!searchTerm) return true;
  
  const term = searchTerm.toLowerCase();
  
  // Check if the search term appears in the product name, description, or material
  return (
    product.name.toLowerCase().includes(term) ||
    (product.description && product.description.toLowerCase().includes(term)) ||
    (product.material && product.material.toLowerCase().includes(term)) ||
    (product.color && product.color.toLowerCase().includes(term))
  );
}
